import { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import styled from 'styled-components'
import { useDismissRecurrenceSuggestion, useEvaluateRecurrenceSuggestion } from '../../services/api/tasks.hooks'
import { useGTQueryClient } from '../../services/queryUtils'
import { Border, Colors, Spacing, Typography } from '../../styles'
import { tokens } from '../../styles/tokens'
import { TTaskV4 } from '../../utils/types'
import GTButton from '../atoms/buttons/GTButton'
import RecurringTaskTemplateModal from '../molecules/recurring-tasks/RecurringTaskTemplateModal'

const Card = styled.section`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${Spacing._12};
    padding: ${Spacing._12};
    border: ${Border.stroke.medium} solid ${Colors.background.hairline};
    border-radius: ${tokens.radius.md};
    background: ${Colors.background.white};
`

const Copy = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._2};
`

const Title = styled.div`
    color: ${Colors.text.title};
    ${Typography.title.small};
`

const Detail = styled.div`
    color: ${Colors.text.muted};
    ${Typography.body.small};
`

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: ${Spacing._8};
`

const RecurrenceSuggestionCard = ({ task }: { task: TTaskV4 }) => {
    const [setupOpen, setSetupOpen] = useState(false)
    const evaluate = useEvaluateRecurrenceSuggestion(task.id)
    const dismiss = useDismissRecurrenceSuggestion(task.id)
    const queryClient = useGTQueryClient()
    const suggestion = task.recurrence_suggestion

    useEffect(() => {
        if (suggestion?.status === 'pending' && !evaluate.isLoading) evaluate.mutate()
    }, [task.id, suggestion?.status])

    useEffect(() => {
        if (suggestion?.status !== 'evaluating') return
        const interval = window.setInterval(() => queryClient.invalidateQueries('tasks_v4'), 1500)
        return () => window.clearInterval(interval)
    }, [queryClient, suggestion?.status])

    if (suggestion?.status !== 'ready' || suggestion.recurrence_rate === undefined || !suggestion.suggested_date) {
        return null
    }

    return (
        <>
            <Card data-testid="recurrence-suggestion">
                <Copy>
                    <Title>Looks like a repeat</Title>
                    <Detail>Set up a schedule for next time.</Detail>
                </Copy>
                <Actions>
                    <GTButton
                        value="Dismiss"
                        aria-label="Dismiss recurrence suggestion"
                        styleType="secondary"
                        disabled={dismiss.isLoading}
                        onClick={() => dismiss.mutate()}
                    />
                    <GTButton value="Set up recurrence" styleType="primary" onClick={() => setSetupOpen(true)} />
                </Actions>
            </Card>
            {setupOpen && (
                <RecurringTaskTemplateModal
                    onClose={() => setSetupOpen(false)}
                    initialTask={task}
                    initialFolderId={task.id_folder}
                    initialRecurrenceRate={suggestion.recurrence_rate}
                    initialSelectedDate={DateTime.fromISO(suggestion.suggested_date)}
                />
            )}
        </>
    )
}

export default RecurrenceSuggestionCard
